import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CreditCard, Download, CheckCircle, Clock } from 'lucide-react';
import { repairerInvoiceApi, RepairerInvoiceListItem } from '../api/repairerInvoiceApi';
import { formatPrice } from '@/utils/priceHelpers';
import MarkPaidDialog from '../pages/RepairerInvoicesPage/MarkPaidDialog';
import { useAuthStore } from '../../../stores/auth-store';

interface Props {
  savId: number;
}

/**
 * Encart « Paiement réparateur » sur le détail SAV outil-pro.
 *
 * Ne rend RIEN tant qu'aucune facture réparateur n'est visible pour ce SAV
 * (statuts pré-SENT_TO_GARANT invisibles par design, SAV hors cycle paiement
 * → liste vide → null). Zéro impact pour les vendeurs sans le workflow.
 *
 * Cf SPECS/repairer_payment/README.md Phase 4.2 (retour pilote 2026-07-23).
 */
export default function RepairerPaymentSection({ savId }: Props) {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const [markPaidTarget, setMarkPaidTarget] = useState<RepairerInvoiceListItem | null>(null);

  // Ne consulte l'endpoint (désormais gaté sav.sign/sav.manage côté backend)
  // que si l'utilisateur a le droit → pas de requête 403 de fond pour les rôles
  // restreints (sav.list/view sans signer/gérer).
  const canSeeRepairerInvoices =
    hasPermission('sav.sign') || hasPermission('sav.manage');

  const { data: invoices } = useQuery({
    queryKey: ['sav-repairer-invoices', savId],
    queryFn: () => repairerInvoiceApi.list(undefined, savId),
    enabled: !!savId && canSeeRepairerInvoices,
    staleTime: 30_000,
  });

  if (!invoices?.length) return null;

  return (
    <div id="paiement" className="tw-mb-4 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <CreditCard size={18} className="tw-text-affinicar-primary" />
        <h3 className="tw-font-semibold tw-text-gray-900">Paiement réparateur</h3>
      </div>

      <div className="tw-space-y-3">
        {invoices.map((inv) => (
          <InvoiceRow
            key={inv.id}
            invoice={inv}
            onMarkPaid={() => setMarkPaidTarget(inv)}
          />
        ))}
      </div>

      {markPaidTarget && (
        <MarkPaidDialog
          invoice={markPaidTarget}
          onClose={() => setMarkPaidTarget(null)}
          onPaid={async () => {
            setMarkPaidTarget(null);
            await queryClient.invalidateQueries({ queryKey: ['sav-repairer-invoices', savId] });
            await queryClient.invalidateQueries({ queryKey: ['repairer-invoices-to-pay-count'] });
          }}
        />
      )}
    </div>
  );
}

const STATUS_DISPLAY: Record<string, { label: string; cls: string }> = {
  SENT_TO_GARANT: { label: 'À régler', cls: 'tw-bg-amber-100 tw-text-amber-800' },
  PAID_BY_GARANT: { label: 'Payée — en attente de confirmation du réparateur', cls: 'tw-bg-blue-100 tw-text-blue-800' },
  // Litige piloté par le gestionnaire Affinicar — label neutre, aucune
  // action vendeur requise dans l'outil.
  PAYMENT_DISPUTED: { label: 'Vérification en cours', cls: 'tw-bg-gray-100 tw-text-gray-600' },
  PAYMENT_CONFIRMED: { label: 'Réception confirmée par le réparateur', cls: 'tw-bg-green-100 tw-text-green-700' },
  CANCELLED: { label: 'Annulée', cls: 'tw-bg-gray-100 tw-text-gray-500' },
};

function InvoiceRow({
  invoice,
  onMarkPaid,
}: {
  invoice: RepairerInvoiceListItem;
  onMarkPaid: () => void;
}) {
  const display = STATUS_DISPLAY[invoice.status] ?? {
    label: invoice.status,
    cls: 'tw-bg-gray-100 tw-text-gray-600',
  };
  const showRib = !!invoice.iban;

  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-font-medium">{formatPrice(invoice.amountTtc)}</span>
          <span className="tw-text-sm tw-text-gray-500">
            Facture {invoice.invoiceNumber}
            {invoice.invoiceDate && ` du ${new Date(invoice.invoiceDate).toLocaleDateString('fr-FR')}`}
          </span>
          {invoice.hasDocument && (
            <button
              type="button"
              title="Télécharger la facture"
              onClick={() => repairerInvoiceApi.downloadDocument(invoice.id, `facture_${invoice.invoiceNumber || invoice.savId}`)}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-text-affinicar-primary tw-text-sm hover:tw-opacity-70"
            >
              <Download size={14} /> Télécharger
            </button>
          )}
        </div>
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${display.cls}`}>
          {invoice.status === 'PAYMENT_CONFIRMED' ? <CheckCircle size={12} /> : <Clock size={12} />}
          {display.label}
        </span>
      </div>

      {showRib && invoice.status === 'SENT_TO_GARANT' && (
        <div className="tw-mt-3 tw-bg-gray-50 tw-rounded tw-p-3 tw-text-sm">
          <div className="tw-text-xs tw-text-gray-500 tw-mb-1">Coordonnées bancaires du réparateur</div>
          <div className="tw-font-mono">{invoice.iban}</div>
          <div className="tw-flex tw-gap-4 tw-text-gray-600 tw-mt-0.5">
            <span>BIC : {invoice.bic}</span>
            <span>Titulaire : {invoice.accountHolder}</span>
          </div>
          {invoice.paymentReferenceSuffix && (
            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
              Référence à indiquer sur le virement : <span className="tw-font-mono tw-font-medium">{invoice.paymentReferenceSuffix}</span>
            </div>
          )}
        </div>
      )}

      {invoice.status === 'SENT_TO_GARANT' && (
        <div className="tw-mt-3 tw-flex tw-justify-end">
          <button
            type="button"
            onClick={onMarkPaid}
            className="tw-bg-affinicar-primary tw-text-white tw-px-3 tw-py-1.5 tw-rounded tw-font-medium tw-text-sm hover:tw-opacity-90"
          >
            J'ai payé
          </button>
        </div>
      )}

      {invoice.status === 'PAID_BY_GARANT' && invoice.paidByGarantAt && (
        <p className="tw-mt-2 tw-text-xs tw-text-gray-500">
          Paiement déclaré le {new Date(invoice.paidByGarantAt).toLocaleDateString('fr-FR')}
          {invoice.paymentReferenceByGarant && ` — réf. ${invoice.paymentReferenceByGarant}`}
        </p>
      )}
    </div>
  );
}
